import React, { useEffect, useState } from 'react';
import { Typography, Row, Col, Spin, Empty } from 'antd';
import { useNavigate } from 'react-router-dom';
import { StyledHomeContainer, StyledCard } from './styles/HomePage.styles';
import apiService from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import * as Icons from '@ant-design/icons';

const { Title, Paragraph } = Typography;

interface InfoBlock {
    id: number;
    title: string;
    description: string | null;
    link: string | null;
    icon: string | null;
    order: number;
    status: 'active' | 'inactive';
}

const getIconComponent = (iconName: string | null) => {
    if (!iconName) return null;
    const IconComponent = (Icons as any)[iconName];
    if (IconComponent && typeof IconComponent === 'function') {
        return React.createElement(IconComponent);
    }
    return null;
};

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [infoBlocks, setInfoBlocks] = useState<InfoBlock[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInfoBlocks = async () => {
      try {
        setLoading(true);
        const response = await apiService.get<ApiResponse<InfoBlock[]>>(
          API_ENDPOINTS.INFO_BLOCKS.BASE
        );
        
        if (response.data?.data && Array.isArray(response.data.data)) {
          // Фильтруем только активные блоки и сортируем по order
          const activeBlocks = response.data.data
            .filter((block: InfoBlock) => block.status === 'active')
            .sort((a: InfoBlock, b: InfoBlock) => a.order - b.order);
          setInfoBlocks(activeBlocks);
        } else {
          setInfoBlocks([]);
        }
      } catch (error) {
        logger.error('Error fetching info blocks:', error);
        // При ошибке оставляем пустой массив
        setInfoBlocks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInfoBlocks();
  }, []);

  const handleCardClick = (block: InfoBlock) => {
    if (block.link && block.link.trim()) {
      const trimmedLink = block.link.trim();
      // Если ссылка начинается с /, используем navigate, иначе открываем в новой вкладке
      if (trimmedLink.startsWith('/')) {
        navigate(trimmedLink);
      } else if (trimmedLink.startsWith('http://') || trimmedLink.startsWith('https://')) {
        window.open(trimmedLink, '_blank', 'noopener,noreferrer');
      }
    }
  };

  return (
    <StyledHomeContainer>
      <Title level={1}>Добро пожаловать в систему управления турнирами по карате</Title>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : infoBlocks.length === 0 ? (
        <Empty description="Блоки информации не найдены" />
      ) : (
        <Row gutter={[24, 24]}>
          {infoBlocks.map((block) => {
            const IconComponent = getIconComponent(block.icon);
            const hasLink = block.link && block.link.trim();
            const cardTitle = (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {IconComponent && <span>{IconComponent}</span>}
                <span>{block.title}</span>
              </div>
            );

            return (
              <Col xs={24} md={8} key={block.id}>
                <StyledCard
                  title={cardTitle}
                  onClick={hasLink ? () => handleCardClick(block) : undefined}
                  $clickable={!!hasLink}
                >
                  {block.description && (
                    <Paragraph>{block.description}</Paragraph>
                  )}
                </StyledCard>
              </Col>
            );
          })}
        </Row>
      )}
    </StyledHomeContainer>
  );
};

export default HomePage;